import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Printer, Search, FileText } from 'lucide-react';
import { getDailyLogs, getProjects, getConsolidatedInvoices, getCustomers } from '../api';
import { buildNumberLookup } from '../lib/customerSearch';
import { toast } from '../hooks/use-toast';

const DebtorsReport = () => {
  const navigate = useNavigate();
  const [debtors, setDebtors] = useState([]);
  const [consolidatedDebts, setConsolidatedDebts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [numberOf, setNumberOf] = useState(() => () => undefined);

  useEffect(() => {
    loadDebtors();
  }, []);

  const loadDebtors = async () => {
    try {
      const [logs, projects, customers] = await Promise.all([getDailyLogs(), getProjects(), getCustomers().catch(() => [])]);
      const lookup = buildNumberLookup(customers);
      setNumberOf(() => lookup);
      
      // Filter logs where accountClosed is false (debtors)
      const debtorLogs = logs.filter(log => !log.accountClosed).map(log => ({
        ...log,
        type: 'daily_log',
        displayName: log.customerName
      }));
      
      // Filter projects where accountClosed is false (debtors)
      const debtorProjects = projects.filter(project => !project.accountClosed).map(project => ({
        ...project,
        type: 'project',
        displayName: project.projectName,
        workItems: project.items // Map items to workItems for consistency
      }));
      
      // Combine both and filter out zero-debt records
      const allDebtors = [...debtorLogs, ...debtorProjects].filter(d => {
        const debt = calculateDebt(d);
        return debt > 0;
      });
      
      setDebtors(allDebtors);

      // Load consolidated invoices with balance
      try {
        const consolidated = await getConsolidatedInvoices();
        const withDebts = consolidated.filter(inv => Math.abs(inv.balance) > 0.01);
        setConsolidatedDebts(withDebts);
      } catch (e) {
        console.error('Error loading consolidated invoices:', e);
      }
    } catch (error) {
      console.error('Error loading debtors:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את רשימת החייבים',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₪${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateTransactionTotal = (workItems) => {
    if (!workItems || workItems.length === 0) return 0;
    return workItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.transactionAmount) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const calculatePaidFromPayments = (payments) => {
    if (!payments || payments.length === 0) return 0;
    return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  };

  const calculateDebt = (debtor) => {
    const transactionTotal = calculateTransactionTotal(debtor.workItems) * (1 + (parseFloat(debtor.vatRate) || 0) / 100);
    const paid = calculatePaidFromPayments(debtor.payments);
    return transactionTotal - paid;
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredDebtors = debtors.filter(d => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase().replace(/^#/, '');
    if (/^\d+$/.test(q) && String(numberOf(d.customerName) ?? '') === q) return true;
    return (d.displayName || '').toLowerCase().includes(q) ||
           (d.customerName || '').toLowerCase().includes(q);
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center print:hidden">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">דווח חייבים</h1>
            <p className="text-gray-600">סך הכל {debtors.length} לקוחות חייבים</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handlePrint}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Printer className="ml-2 w-5 h-5" />
              הדפסה
            </Button>
            <Button
              onClick={() => navigate('/reports')}
              variant="outline"
              className="border-2 hover:bg-gray-50 transition-colors"
            >
              <ArrowRight className="ml-2 w-5 h-5" />
              חזור לדווח
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6 print:hidden">
          <div className="relative max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="חיפוש לפי שם או מספר לקוח..."
              className="w-full pr-10 pl-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors bg-white"
              data-testid="debtor-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg font-bold"
                data-testid="clear-search-btn"
              >
                ×
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              מציג {filteredDebtors.length} מתוך {debtors.length} חייבים
            </p>
          )}
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">דווח חייבים</h1>
          <p className="text-gray-600">תאריך הדפסה: {new Date().toLocaleDateString('he-IL')}</p>
        </div>

        {/* Consolidated Invoice Balances */}
        {consolidatedDebts.length > 0 && (
          <Card className="mb-6 shadow-xl border-0 print:shadow-none" data-testid="consolidated-debts-section">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white print:bg-gray-200 print:text-black">
              <CardTitle className="text-xl flex items-center gap-2">
                <FileText className="w-5 h-5" />
                הפרשים בחשבונות מרוכזים ({consolidatedDebts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {consolidatedDebts.map(inv => (
                  <div
                    key={inv.id}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${
                      inv.balance > 0 ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                    }`}
                    onClick={() => navigate(`/consolidated-invoice?id=${inv.id}`)}
                  >
                    <div>
                      {numberOf(inv.customerName) && <span className="font-mono font-bold text-gray-500 ml-2">#{numberOf(inv.customerName)}</span>}
                      <span className="font-bold text-gray-900">{inv.customerName}</span>
                      <span className="text-sm text-gray-500 mr-3">
                        {inv.logIds.length} רישומים | סה"כ: {formatCurrency(inv.totalAmount)}
                      </span>
                    </div>
                    <Badge className={inv.balance > 0 ? 'bg-red-600' : 'bg-blue-600'}>
                      {inv.balance > 0 ? `חוב: ${formatCurrency(inv.balance)}` : `עודף: ${formatCurrency(Math.abs(inv.balance))}`}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Debtors Report */}
        {filteredDebtors.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              {searchQuery ? (
                <p className="text-xl text-gray-500">לא נמצאו חייבים התואמים לחיפוש &quot;{searchQuery}&quot;</p>
              ) : (
                <p className="text-xl text-green-600 font-semibold">אין חייבים! כל החשבונות סגורים</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="shadow-2xl border-0 bg-gradient-to-r from-red-500 to-orange-500 text-white print:bg-white print:text-black print:border print:border-gray-300">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-lg font-semibold mb-2">סך הכל חוב</p>
                  <p className="text-4xl font-bold">
                    {formatCurrency(
                      filteredDebtors.reduce((total, debtor) => total + calculateDebt(debtor), 0)
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Debtors Table */}
            <Card className="shadow-2xl border-0 print:shadow-none print:border print:border-gray-300">
              <CardHeader className="bg-gradient-to-r from-red-600 to-orange-600 text-white print:bg-gray-100 print:text-black">
                <CardTitle className="text-2xl font-bold">רשימת חייבים</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-red-500 to-orange-500 text-white print:bg-gray-200 print:text-black">
                        <th className="border border-gray-300 p-3 text-center font-bold">תאריך</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">סוג</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">שם</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">מס' לקוח</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">לקוח</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">פירוט עבודה</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">סכום עסקה</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">תשלומים</th>
                        <th className="border border-gray-300 p-3 text-center font-bold">סכום חוב</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDebtors.map((debtor, index) => (
                        <tr 
                          key={debtor.id} 
                          className={index % 2 === 0 ? 'bg-white hover:bg-red-50' : 'bg-gray-50 hover:bg-red-50'}
                        >
                          <td className="border border-gray-300 p-3 text-center whitespace-nowrap align-top">
                            {formatDate(debtor.date)}
                          </td>
                          <td className="border border-gray-300 p-3 text-center align-top">
                            <Badge 
                              className={`cursor-pointer transition-transform hover:scale-110 ${debtor.type === 'project' ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
                              onClick={() => {
                                if (debtor.type === 'project') {
                                  navigate(`/project-execution-steps/${debtor.id}`);
                                } else {
                                  navigate(`/daily-log?id=${debtor.id}`);
                                }
                              }}
                              data-testid={`debtor-type-link-${debtor.id}`}
                            >
                              {debtor.type === 'project' ? 'פרויקט' : 'רישום יומי'} ←
                            </Badge>
                          </td>
                          <td className="border border-gray-300 p-3 text-center font-semibold align-top">
                            {debtor.displayName}
                          </td>
                          <td className="border border-gray-300 p-3 text-center align-top font-mono font-bold text-gray-700" data-testid={`debtor-number-${debtor.id}`}>
                            {numberOf(debtor.customerName) ?? '-'}
                          </td>
                          <td className="border border-gray-300 p-3 text-center align-top">
                            {debtor.customerName}
                          </td>
                          <td className="border border-gray-300 p-3">
                            {debtor.workItems && debtor.workItems.length > 0 ? (
                              <table className="w-full border-collapse text-sm">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="border border-gray-200 p-2 text-right">שם חלק</th>
                                    <th className="border border-gray-200 p-2 text-center">כמות</th>
                                    <th className="border border-gray-200 p-2 text-center">סכום עסקה</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {debtor.workItems.map((item, idx) => (
                                    <tr key={idx}>
                                      <td className="border border-gray-200 p-2 text-right">{item.partName}</td>
                                      <td className="border border-gray-200 p-2 text-center">{item.quantity}</td>
                                      <td className="border border-gray-200 p-2 text-center text-blue-700 font-semibold">
                                        {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.transactionAmount) || 0))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="text-gray-400 text-center">אין פריטים</div>
                            )}
                          </td>
                          <td className="border border-gray-300 p-3 text-center font-bold text-blue-700 text-lg align-top">
                            {formatCurrency(calculateTransactionTotal(debtor.workItems) * (1 + (parseFloat(debtor.vatRate) || 0) / 100))}
                          </td>
                          <td className="border border-gray-300 p-3 text-center align-top">
                            {debtor.payments && debtor.payments.length > 0 ? (
                              <span className="font-semibold text-green-700">
                                {formatCurrency(calculatePaidFromPayments(debtor.payments))}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="border border-gray-300 p-3 text-center font-bold text-red-700 text-lg align-top">
                            {formatCurrency(calculateDebt(debtor))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:bg-white {
            background-color: #fff !important;
          }
          .print\\:bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .print\\:bg-gray-200 {
            background-color: #e5e7eb !important;
          }
          .print\\:text-black {
            color: #000 !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:border {
            border: 1px solid #d1d5db !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
};

export default DebtorsReport;
